import { Fragment, useEffect, useState } from 'react';
import type { AuditEntry } from '../types.js';
import { listAudit } from '../api.js';

export default function AuditLog() {
  const [entries, setEntries] = useState<AuditEntry[]>([]);
  const [page, setPage] = useState(1);
  const [actor, setActor] = useState('');
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [expandedId, setExpandedId] = useState<number | null>(null);

  async function load() {
    setLoading(true);
    setError('');
    try {
      const res = await listAudit({
        page,
        actor: actor || undefined,
        action: action || undefined,
      });
      setEntries(res.entries);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to load');
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { void load(); }, [page, actor, action]);

  return (
    <>
      <div className="page-header">
        <h1 className="page-title">Audit Log</h1>
      </div>

      <div className="card">
        <div style={{ display: 'flex', gap: '.75rem', marginBottom: '1rem', flexWrap: 'wrap' }}>
          <input
            type="text"
            placeholder="Filter by actor email…"
            value={actor}
            onChange={(e) => { setActor(e.target.value); setPage(1); }}
            style={{ maxWidth: '240px' }}
          />
          <input
            type="text"
            placeholder="Filter by action…"
            value={action}
            onChange={(e) => { setAction(e.target.value); setPage(1); }}
            style={{ maxWidth: '200px' }}
          />
        </div>

        {error && <div className="alert alert-error">{error}</div>}
        {loading ? (
          <p style={{ color: '#94a3b8' }}>Loading…</p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ tableLayout: 'fixed', minWidth: '900px' }}>
              <colgroup>
                <col style={{ width: '160px' }} />
                <col style={{ width: '70px' }} />
                <col style={{ width: '200px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '180px' }} />
                <col style={{ width: '120px' }} />
                <col />
              </colgroup>
              <thead>
                <tr>
                  <th>Time</th>
                  <th>Status</th>
                  <th>Actor</th>
                  <th>Action</th>
                  <th>Target</th>
                  <th>IP</th>
                  <th>Details</th>
                </tr>
              </thead>
              <tbody>
                {entries.length === 0 && (
                  <tr><td colSpan={7} style={{ color: '#94a3b8', padding: '1rem 0' }}>No entries found.</td></tr>
                )}
                {entries.map((e) => {
                  const isExpanded = expandedId === e.id;
                  const hasDetails = !!e.details;
                  return (
                    <Fragment key={e.id}>
                      <tr
                        onClick={() => hasDetails && setExpandedId(isExpanded ? null : e.id)}
                        style={{ cursor: hasDetails ? 'pointer' : 'default' }}
                      >
                        <td style={{ whiteSpace: 'nowrap', fontSize: '.78rem' }}>
                          {new Date(e.created_at * 1000).toLocaleString('en-AU')}
                        </td>
                        <td>
                          <span className="badge badge-green">✓ OK</span>
                        </td>
                        <td style={{ fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.actor}>
                          {e.actor}
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.action}>
                          <code style={{ fontSize: '.78rem' }}>{e.action}</code>
                        </td>
                        <td style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.target_id ?? ''}>
                          <code style={{ fontSize: '.78rem' }}>{e.target_id ?? '—'}</code>
                        </td>
                        <td style={{ fontSize: '.8rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={e.ip ?? ''}>
                          {e.ip ?? '—'}
                        </td>
                        <td style={{ fontSize: '.78rem', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', color: '#64748b' }}>
                          {hasDetails ? (isExpanded ? '▾ hide' : '▸ click to view') : '—'}
                        </td>
                      </tr>
                      {isExpanded && hasDetails && (
                        <tr>
                          <td colSpan={7} style={{ background: '#f8fafc', padding: '1rem' }}>
                            <pre style={{
                              background: '#0f172a',
                              color: '#e2e8f0',
                              padding: '.75rem',
                              borderRadius: '4px',
                              fontSize: '.8rem',
                              overflow: 'auto',
                              margin: 0,
                              whiteSpace: 'pre-wrap',
                              wordBreak: 'break-word',
                            }}>{JSON.stringify(e.details, null, 2)}</pre>
                          </td>
                        </tr>
                      )}
                    </Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        <div className="pagination">
          <button onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>← Prev</button>
          <span>Page {page}</span>
          <button onClick={() => setPage((p) => p + 1)} disabled={entries.length < 50}>Next →</button>
        </div>
      </div>
    </>
  );
}
